import type { Dispatch, ReactNode, SetStateAction } from 'react';
import type { FileRejection } from 'react-dropzone';

import type { IRGB } from '@picsart/web-layering/types';

import type { BATCH_ACTIONS, BATCH_TOOLS, IMAGE_MIME_TYPES } from 'pages/Batch/configs';
import type { IResizeApplyAttrs, IResizeParams, IWatermarkApplyAttrs, IWatermarkAttrs, TApplyBackgroundProp } from 'pages/Batch/types';
import type { ItemSourceEnum } from 'pages/Batch/utils/analytics';

import type { BatchDataStatus } from 'configs/constants';

import type { ICropApplyAttrs, IEnhanceApplyAttrs, IBatchData, IUserBackground, BottomSheetConfig, StatusIndicatorMode, IBgToolRatio, IForegroundTransform, IForegroundContentBbox } from 'types/store';
import type { IWfxAttrs } from '../Sidebar/WfxTool/types';
import type { IAdjustAttrs } from '../Sidebar/AdjustTool/types';
import type { IComposedEnhanceAttrs } from '../Sidebar/AIEnhanceTool/types';

// Mirrors the ExpandStates enum of the retired @picsart/editorlayout bottom sheet.
export enum ExpandStates {
  Full = 'Full',
  Default = 'Default',
  Minimized = 'Minimized',
  Closed = 'Closed',
}

export enum ActionTypes {
  UPDATE_STATUSES = 'UPDATE_STATUSES',
  ADD_DATA = 'ADD_DATA',
  SET_DATA = 'SET_DATA',
  PUT_DATA = 'PUT_DATA',
  DELETE_DATA = 'DELETE_DATA',
  RESET_DATA = 'RESET_DATA',
  APPLY_PENDING = 'APPLY_PENDING',
  DISCARD_PENDING = 'DISCARD_PENDING',
  DELETE_ALL_DATA = 'DELETE_ALL_DATA',
  SET_RESIZE_PARAMS = 'SET_RESIZE_PARAMS',
  UPDATE_IMAGES_DATA = 'UPDATE_IMAGES_DATA',
}

interface UpdateStatuses {
  type: typeof ActionTypes.UPDATE_STATUSES;
  status: BatchDataStatus;
  statusIndicatorMode: StatusIndicatorMode;
  skipItemsWithError: boolean;
  itemKeys?: string[];
}

interface AddData {
  type: typeof ActionTypes.ADD_DATA;
  items: IBatchData[];
}

interface PutData {
  type: typeof ActionTypes.PUT_DATA;
  item: IBatchData;
}

interface UpdateImagesData {
  type: typeof ActionTypes.UPDATE_IMAGES_DATA;
  items: IBatchData[];
}

interface DeleteData {
  type: typeof ActionTypes.DELETE_DATA;
  keys: string[];
}

interface ResetData {
  type: typeof ActionTypes.RESET_DATA;
  keys: string[];
}

interface ApplyPending {
  type: typeof ActionTypes.APPLY_PENDING;
  selectedPhotoIds: string[];
}

interface DiscardPending {
  type: typeof ActionTypes.DISCARD_PENDING;
}

interface DeleteAllData {
  type: typeof ActionTypes.DELETE_ALL_DATA;
}

interface ResizeParams {
  type: typeof ActionTypes.SET_RESIZE_PARAMS;
  params: IResizeParams;
}

export type DataActions = AddData | UpdateStatuses | PutData | DeleteData | ResetData | ApplyPending | DiscardPending | DeleteAllData | ResizeParams | UpdateImagesData;

export interface IBatchAttrs {
  [key: string]:
    | string
    | boolean
    | number
    | null
    | IRGB
    | HTMLImageElement
    | Record<string, number>
    | Record<string, IForegroundTransform>
    | Record<string, IForegroundContentBbox>
    | IBgToolRatio
    | IForegroundTransform
    | IForegroundContentBbox
    | undefined;
}

export interface IBatchContext {
  data: IBatchData[];
  process: (
    action: BATCH_ACTIONS,
    attrs: IBatchAttrs | IEnhanceApplyAttrs | ICropApplyAttrs | IWatermarkAttrs | IWatermarkApplyAttrs | TApplyBackgroundProp | IResizeApplyAttrs | IWfxAttrs | IAdjustAttrs | IComposedEnhanceAttrs,
    isApply?: boolean,
    targetData?: IBatchData[],
  ) => void;
  processItems: (
    action: BATCH_ACTIONS,
    attrs: IBatchAttrs | IEnhanceApplyAttrs | ICropApplyAttrs | IWatermarkAttrs | IWfxAttrs | IAdjustAttrs | IComposedEnhanceAttrs,
    keys: string[],
  ) => Promise<void>;
  recordStep: (action: BATCH_ACTIONS, attrs: Record<string, any>) => void;
  userBackgrounds: IUserBackground[];
  addUserBackground: (photo: string) => IUserBackground | void;
  dispatchData: Dispatch<DataActions>;
  selectedKeys: string[];
  selectedData: IBatchData[];
  setSelectedKeys: Dispatch<SetStateAction<string[]>>;
  toolWorkingKeys: string[];
  workingData: IBatchData[];
  isSuccessModal: boolean;
  setIsSuccessModal: Dispatch<SetStateAction<boolean>>;
  isZipFile: boolean;
  setIsZipFile: Dispatch<SetStateAction<boolean>>;
  discardPending: () => void;
  // Returns the current processing epoch. Non-Toolchain async flows capture it at start and re-check before
  // writing a result; a changed value means discardPending (Cancel) ran, so the stale result must be dropped.
  getProcessingEpoch: () => number;
  retryItem: (itemKey: string) => Promise<void>;
  allSelected: boolean;
  setUploadedData: Dispatch<SetStateAction<{ count: number; status: BatchDataStatus }>>;
  uploadedData: { count: number; status: BatchDataStatus };
  acceptFiles: (files: File[], rejectedFiles: FileRejection[], isDrop?: boolean, itemSource?: ItemSourceEnum, callback?: () => void) => void;
  setActiveTool: Dispatch<SetStateAction<BATCH_TOOLS | null>>;
  activeTool: BATCH_TOOLS | null;
  setMultiplyFactor: Dispatch<SetStateAction<number | null>>;
  multiplyFactor: number | null;
  resizeParams: IResizeParams;
  resetResizeParams: () => void;
  expandedState: ExpandStates;
  isMobile: boolean;
  setExpandState: Dispatch<SetStateAction<ExpandStates>>;
  isLoading: boolean;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  isRestoring: boolean;
  bottomSheetConfig: BottomSheetConfig;
  setBottomSheetConfig: Dispatch<SetStateAction<BottomSheetConfig>>;
  imageFormat: IMAGE_MIME_TYPES;
  confirmModalParams: ConfirmModalParams;
  setImageFormat: Dispatch<SetStateAction<IMAGE_MIME_TYPES>>;
  showDeleteItemConfirmModal: boolean;
  setShowDeleteItemConfirmModal: Dispatch<SetStateAction<boolean>>;
  setConfirmModalParams: Dispatch<SetStateAction<ConfirmModalParams>>;
  setIsErrorMessageShown: Dispatch<SetStateAction<boolean>>;
  areImagesResizing: boolean;
  isErrorMessageShown: boolean;
  initialRenderComplete: boolean;
  fileExportInfo: FileExportInfo;
  setFileExportInfo: Dispatch<SetStateAction<FileExportInfo>>;
  myFilesFolderUid: string;
  setMyFilesFolderUid: Dispatch<SetStateAction<string>>;
}

export interface IBatchContextProviderProps {
  children: ReactNode;
}

export enum ActionType {
  FULL_STORAGE = 'FULL_STORAGE',
  FULL_STORAGE_PRO = 'FULL_STORAGE_PRO',
}

export interface ConfirmModalParams {
  show: boolean;
  selectedAction?: ActionType | null;
}

export enum ExportType {
  DRIVE = 'DRIVE',
  DEVICE = 'DEVICE',
}

export type FileExportInfo = {
  count: number;
  exportType: ExportType | null;
};
